import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
  ChevronLeft, ChevronRight, Clock, Globe, Calendar, Loader2, Check,
  ArrowLeft, User, Mail, Phone, MessageSquare, AlertCircle, CheckCircle2,
  Video
} from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isBefore, startOfDay, getDay } from 'date-fns';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/utils';

interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

interface DayAvailability {
  date: string;
  slots: TimeSlot[];
  isAvailable: boolean;
}

interface SchedulingPage {
  id: string;
  title: string;
  description?: string;
  duration: number;
  timezone: string;
  color?: string;
  questions: Array<{
    id: string;
    question: string;
    type: string;
    isRequired: boolean;
    options?: string[];
  }>;
}

type BookingStep = 'calendar' | 'time' | 'form' | 'success';

// Format a slot range: "9:00 – 9:30 AM"
const fmtSlotRange = (start: string, end: string) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const startStr = `${sh % 12 || 12}:${String(sm).padStart(2, '0')}`;
  const endStr = `${eh % 12 || 12}:${String(em).padStart(2, '0')} ${eh >= 12 ? 'PM' : 'AM'}`;
  return `${startStr} – ${endStr}`;
};

const WEEK_DAYS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEK_DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const BOOKING_STEPS = [
  { key: 'calendar' as const, label: 'Select Date' },
  { key: 'time' as const, label: 'Select Time' },
  { key: 'form' as const, label: 'Your Details' },
];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [page, setPage] = useState<SchedulingPage | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<DayAvailability[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<BookingStep>('calendar');

  // Form state
  const [formData, setFormData] = useState({
    attendeeName: '',
    attendeeEmail: '',
    attendeePhone: '',
    attendeeNotes: '',
    answers: {} as Record<string, string>,
  });

  useEffect(() => {
    loadPage();
  }, [slug]);

  useEffect(() => {
    if (page) {
      loadAvailability();
    }
  }, [page, currentMonth]);

  const loadPage = async () => {
    try {
      setLoading(true);
      const data = await api.getPublicPage(slug!);
      if (data.error) {
        setError(data.error);
      } else {
        setPage(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load scheduling page');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async () => {
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const data = await api.getPublicAvailability(slug!, start, end);
      setAvailability(data);
    } catch (err: any) {
      console.error('Failed to load availability:', err);
    }
  };

  const getDayAvailability = (date: Date): DayAvailability | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => a.date === dateStr);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep('time');
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep('form');
  };

  const handleBack = () => {
    if (step === 'form') {
      setStep('time');
      setSelectedSlot(null);
    } else if (step === 'time') {
      setStep('calendar');
      setSelectedDate(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot || !page) return;

    try {
      setSubmitting(true);
      setError('');

      const [hours, minutes] = selectedSlot.startTime.split(':').map(Number);
      const startTime = new Date(selectedDate);
      startTime.setHours(hours, minutes, 0, 0);

      const answers = Object.entries(formData.answers).map(([questionId, answer]) => ({
        questionId,
        answer,
      }));

      const bookingData: any = {
        attendeeName: formData.attendeeName,
        attendeeEmail: formData.attendeeEmail,
        attendeePhone: formData.attendeePhone || undefined,
        attendeeNotes: formData.attendeeNotes || undefined,
        startTime: startTime.toISOString(),
        timezone: page.timezone,
        answers,
      };

      if (token) {
        bookingData.token = token;
      }

      const result = await api.createBooking(slug!, bookingData);

      if (result.error) {
        throw new Error(result.error);
      }

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Failed to create booking');
      setSubmitting(false);
    }
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!page) return null;

  const accentColor = page.color || '#2563eb';

  // ── Success ──────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Booking Confirmed!</h2>
          <p className="text-gray-600 mb-6">
            We've sent a confirmation email to{' '}
            <span className="font-semibold text-gray-900">{formData.attendeeEmail}</span>{' '}
            with all the details.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-3">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
              <span className="text-gray-800 font-medium">{format(selectedDate!, 'EEEE, MMMM d, yyyy')}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
              <span className="text-gray-800 font-medium">{fmtSlotRange(selectedSlot!.startTime, selectedSlot!.endTime)}</span>
            </div>
            <div className="flex items-center">
              <Globe className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
              <span className="text-gray-800 font-medium">{page.timezone}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
              <span className="text-gray-800 font-medium">{page.duration} minutes</span>
            </div>
          </div>

          <Button
            onClick={() => window.location.reload()}
            className="w-full"
            variant="outline"
          >
            Book Another Meeting
          </Button>
        </div>
      </div>
    );
  }

  // ── Leading empty cells for calendar grid alignment ──────────────────────
  const leadingDays = getDay(startOfMonth(currentMonth));

  // ── Main booking layout ──────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 lg:py-12">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex flex-col lg:flex-row">

          {/* ── Left Sidebar ── */}
          <aside className="w-full lg:w-80 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200">
            {/* Accent bar */}
            <div className="h-1" style={{ backgroundColor: accentColor }} />

            <div className="p-5 lg:p-7 lg:sticky lg:top-0">
              {/* Title */}
              <h1 className="text-lg font-bold text-gray-900 mb-1">{page.title}</h1>

              {/* Description — desktop only */}
              {page.description && (
                <p className="text-gray-500 text-sm mb-5 leading-relaxed hidden lg:block">
                  {page.description}
                </p>
              )}

              {/* Info rows — desktop only */}
              <div className="hidden lg:flex flex-col space-y-3 mb-5">
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-3 text-gray-400 flex-shrink-0" />
                  <span>{page.duration} minutes</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Globe className="h-4 w-4 mr-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{page.timezone}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Video className="h-4 w-4 mr-3 text-gray-400 flex-shrink-0" />
                  <span>Video call details sent after booking</span>
                </div>
              </div>

              {/* Booking summary — desktop only, shown once date is selected */}
              {selectedDate && (
                <div className="hidden lg:block border-t border-gray-100 pt-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Selected
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start text-sm text-gray-700">
                      <Calendar className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                      <span>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                    </div>
                    {selectedSlot && (
                      <div className="flex items-center text-sm text-gray-700">
                        <Clock className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span>{fmtSlotRange(selectedSlot.startTime, selectedSlot.endTime)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Back button — desktop only */}
              {(step === 'time' || step === 'form') && (
                <button
                  onClick={handleBack}
                  className="hidden lg:flex items-center text-sm text-gray-500 hover:text-gray-700 mt-6 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
              )}
            </div>
          </aside>

          {/* ── Right Main ── */}
          <main className="flex-1 p-5 sm:p-8">

            {/* Mobile back button */}
            {(step === 'time' || step === 'form') && (
              <button
                onClick={handleBack}
                className="lg:hidden flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </button>
            )}

            {/* ── Step Indicator ── */}
            <div className="flex items-center mb-8">
              {BOOKING_STEPS.map((s, index) => {
                const isActive = step === s.key;
                const isCompleted =
                  (['time', 'form'] as BookingStep[]).includes(step) && s.key === 'calendar' ||
                  step === 'form' && s.key === 'time';

                return (
                  <div key={s.key} className="flex items-center">
                    <div
                      className={cn(
                        'flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        isCompleted && 'bg-green-50 text-green-700',
                        !isActive && !isCompleted && 'bg-gray-100 text-gray-400',
                      )}
                      style={isActive ? { backgroundColor: accentColor + '18', color: accentColor } : undefined}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold',
                          isCompleted && 'bg-green-500 text-white',
                          !isActive && !isCompleted && 'bg-gray-300 text-gray-500',
                        )}
                        style={isActive ? { backgroundColor: accentColor, color: 'white' } : undefined}
                      >
                        {isCompleted ? <Check className="h-3 w-3" /> : index + 1}
                      </div>
                      <span className="hidden sm:inline">{s.label}</span>
                    </div>
                    {index < 2 && (
                      <div className="w-6 h-px bg-gray-200 mx-1" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Calendar Step ── */}
            {step === 'calendar' && (
              <div>
                {/* Month navigation */}
                <div className="flex items-center justify-between mb-5">
                  <button
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <h3 className="text-base font-semibold text-gray-900">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h3>
                  <button
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  </button>
                </div>

                {/* Weekday headers */}
                <div className="grid grid-cols-7 mb-1">
                  {WEEK_DAYS_FULL.map((day, i) => (
                    <div key={i} className="text-center py-2">
                      <span className="hidden sm:inline text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        {day}
                      </span>
                      <span className="sm:hidden text-xs font-semibold text-gray-400 uppercase">
                        {WEEK_DAYS_SHORT[i]}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Leading empty cells to align first day */}
                  {Array.from({ length: leadingDays }).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}

                  {days.map((day) => {
                    const dayAvail = getDayAvailability(day);
                    const slotCount = dayAvail?.slots?.length || 0;
                    const hasSlots = slotCount > 0;
                    const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                    const isPast = isBefore(day, startOfDay(new Date()));
                    const todayDay = isToday(day);

                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => hasSlots && !isPast && handleDateSelect(day)}
                        disabled={!hasSlots || isPast}
                        className={cn(
                          'aspect-square rounded-lg text-sm font-medium transition-all flex flex-col items-center justify-center gap-0.5 p-1',
                          isPast && 'text-gray-300 cursor-not-allowed opacity-40',
                          !isPast && !hasSlots && 'text-gray-300 cursor-not-allowed',
                          hasSlots && !isPast && !isSelected && 'bg-white border border-gray-200 hover:shadow-sm cursor-pointer',
                          isSelected && 'text-white shadow-md',
                        )}
                        style={{
                          ...(todayDay && !isSelected
                            ? { outline: `2px solid ${accentColor}`, outlineOffset: '1px' }
                            : {}),
                          ...(isSelected ? { backgroundColor: accentColor } : {}),
                        }}
                        onMouseEnter={e => {
                          if (hasSlots && !isPast && !isSelected) {
                            e.currentTarget.style.borderColor = accentColor;
                          }
                        }}
                        onMouseLeave={e => {
                          if (hasSlots && !isPast && !isSelected) {
                            e.currentTarget.style.borderColor = '';
                          }
                        }}
                      >
                        <span>{format(day, 'd')}</span>
                        {hasSlots && !isSelected && !isPast && (
                          <div className="flex space-x-0.5">
                            {Array.from({ length: Math.min(slotCount, 3) }).map((_, i) => (
                              <span
                                key={i}
                                className="w-1 h-1 rounded-full"
                                style={{ backgroundColor: accentColor }}
                              />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="text-center text-gray-400 mt-5 text-xs">
                  Dates with available times are highlighted
                </p>
              </div>
            )}

            {/* ── Time Step ── */}
            {step === 'time' && selectedDate && (
              <div>
                {(() => {
                  const dayAvail = getDayAvailability(selectedDate);
                  const count = dayAvail?.slots?.length || 0;
                  return (
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">
                      {count} time{count !== 1 ? 's' : ''} available
                    </h2>
                  );
                })()}
                <p className="text-gray-500 text-sm mb-6">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(() => {
                    const dayAvail = getDayAvailability(selectedDate);
                    if (!dayAvail || !dayAvail.slots || dayAvail.slots.length === 0) {
                      return (
                        <div className="col-span-full text-center py-8 text-gray-500">
                          No available times for this date
                        </div>
                      );
                    }
                    return dayAvail.slots.map((slot, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSlotSelect(slot)}
                        className="py-3 px-3 bg-white border-2 border-gray-200 rounded-xl text-center font-medium text-sm text-gray-700 transition-all"
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = accentColor;
                          e.currentTarget.style.backgroundColor = accentColor + '0d';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = '';
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        {fmtSlotRange(slot.startTime, slot.endTime)}
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* ── Form Step ── */}
            {step === 'form' && selectedDate && selectedSlot && (
              <div className="max-w-lg">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Details</h2>

                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="h-4 w-4 inline mr-2" />
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.attendeeName}
                      onChange={(e) => setFormData({ ...formData, attendeeName: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.attendeeEmail}
                      onChange={(e) => setFormData({ ...formData, attendeeEmail: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.attendeePhone}
                      onChange={(e) => setFormData({ ...formData, attendeePhone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  {page.questions.map((question) => (
                    <div key={question.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {question.question}
                        {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {question.type === 'TEXTAREA' ? (
                        <textarea
                          rows={3}
                          required={question.isRequired}
                          value={formData.answers[question.id] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            answers: { ...formData.answers, [question.id]: e.target.value },
                          })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        />
                      ) : question.type === 'SELECT' && question.options ? (
                        <select
                          required={question.isRequired}
                          value={formData.answers[question.id] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            answers: { ...formData.answers, [question.id]: e.target.value },
                          })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        >
                          <option value="">Select an option</option>
                          {question.options.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={question.type === 'EMAIL' ? 'email' : question.type === 'PHONE' ? 'tel' : 'text'}
                          required={question.isRequired}
                          value={formData.answers[question.id] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            answers: { ...formData.answers, [question.id]: e.target.value },
                          })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        />
                      )}
                    </div>
                  ))}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="h-4 w-4 inline mr-2" />
                      Additional Notes
                    </label>
                    <textarea
                      rows={3}
                      value={formData.attendeeNotes}
                      onChange={(e) => setFormData({ ...formData, attendeeNotes: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      placeholder="Anything else you'd like to share..."
                    />
                  </div>

                  <Button
                    type="submit"
                    isLoading={submitting}
                    className="w-full py-4 text-lg"
                  >
                    Confirm Booking
                  </Button>

                  <p className="text-center text-sm text-gray-500">
                    By confirming, you agree to receive confirmation and reminder emails.
                  </p>
                </form>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
